import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  useToastProvider,
} from "@deere/fuel-react";
import { useHistory, useParams } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { isDatePast, toCapitalizeSplitStr } from "../shared/utils";
import { useForm } from "react-hook-form";
import { CampaignSuppliers } from "./CampaignSuppliers";
import { CampaignCriteria } from "./CampaignCriteria";
import { CAMPAIGN_STEPS } from "../shared/Constants";
import { useAuthConfig } from "../shared/helper";
import { Loading } from "../shared/Loading";
import { ModalDialog } from "../shared/ModalDialog";
import "../assets/ListItem.scss";
import { CampaignStakeHolders } from "./CampaignStakeholders";
import { CampaignReview } from "./CampaignReview";
import { CampaignPublish } from "./CampaignPublish";
import { StepperComponent } from "../shared/StepperComponent";
import PropTypes from "prop-types";
import { UserContext } from "./Main";
import { useLocation } from "react-router-dom";

export const CampaignCreate = ({ name }) => {
  const location = useLocation();
  let history = useHistory();
  let { editCampaignId } = useParams();
  const { authState } = useOktaAuth();
  let authAxios = useAuthConfig();
  const { enqueueToast } = useToastProvider();
  const info = useContext(UserContext);
  const loggedInUserId = info && info.userId;
  const isCriteriaAdmin = info && info.isCriteriaAdmin;
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm();
  const initState = {
    supplierName: location.state?.supplierName ?? "",
    supplierNumber: location.state?.supplierNo ?? "",
    period: "",
    fiscalYear: "",
    qualityCriteriaData: "",
    deliverCriteriaData: "",
    processAlignmentCriteriaData: "",
    relationshipCriteriaData: "",
    valueCreationCriteriaData: "",
    criteriaSelectedData: "",
    description: "",
    title: "",
    status: "",
    isLoading: true,
  };
  const [isNext, setIsNext] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingNext, setIsSavingNext] = useState(false);
  const [state, setState] = useState(initState);
  const [activeStep, setActiveStep] = useState(0);
  const [qualityCriteriaObj, setQualityCriteriaObj] = useState([]);
  const [deliveryCriteriaObj, setDeliveryCriteriaObj] = useState([]);
  const [processCriteriaObj, setProcessCriteriaObj] = useState([]);
  const [relationCriteriaObj, setRelationCriteriaObj] = useState([]);
  const [valueCriteriaObj, setValueCriteriaObj] = useState([]);
  const [campaignId, setCampaignId] = useState();
  const [stepId, setStepId] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [stakeholderErrorMsg, setStakeholderErrorMsg] = useState("");
  const [isCriteriaError, setIsCriteriaError] = useState(false);
  const [nextButtonName, setNextButtonName] = useState("Save & Next");
  const [disable, setDisable] = useState(false);
  const [supplierFunctionalArea, setSupplierFunctionalArea] = useState(null);
  const [supplierSubFunctionalArea, setSupplierSubFunctionalArea] =
    useState(null);
  const [stakeholderList, setStakeholderList] = useState([]);
  const [selectedStakeholders, setSelectedStakeholders] = useState([]);
  const [ratingTeam, setRatingTeam] = useState([]);

  const getRatingTeam = async (suppNum) => {
    if (suppNum) {
      try {
        const ratingTeamResponse = await authAxios.get(`/teams/${suppNum}`);
        setRatingTeam(ratingTeamResponse?.data?.teamMembers);
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (state?.supplierNumber) {
      getRatingTeam(state?.supplierNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.supplierNumber]);

  const getStakeholdersList = async () => {
    const trueCampaignId = campaignId ?? editCampaignId;
    try {
      return await authAxios.get(`/stakeholders/${trueCampaignId}`);
    } catch (e) {
      console.error(e);
    }
  };

  const processStakeHoldersList = (data) => {
    const updatedList = [];
    const selectedIds = [];
    for (let item of data) {
      if (Object.keys(item.user).length !== 0) {
        selectedIds.push(item.id);
      }
    }
    data.forEach((item) => {
      if (Object.keys(item.user).length !== 0) {
        const searchHelperObj = {
          description: `${item.user.lastName},${item.user.firstName}-${item.user.racfID}`,
        };
        item.user = { ...item.user, ...searchHelperObj };
      }
      let isUnitEmpty = { isEmpty: true };
      if (item.unit && Object.keys(item.unit).length !== 0) {
        isUnitEmpty = { isEmpty: false };
        item.unit = { ...item.unit, ...isUnitEmpty };
      } else item.unit = { ...item.unit, ...isUnitEmpty };
      updatedList.push(item);
    });
    setSelectedStakeholders(selectedIds);
    setStakeholderList(updatedList);
  };

  useEffect(() => {
    getStakeholdersList().then((res) => {
      processStakeHoldersList(res?.data ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, campaignId, editCampaignId, activeStep]);

  const handleCriteriaSelect = (categoryId, data) => {
    if (categoryId === 1) {
      setQualityCriteriaObj(data);
    } else if (categoryId === 2) {
      setDeliveryCriteriaObj(data);
    } else if (categoryId === 3) {
      setProcessCriteriaObj(data);
    } else if (categoryId === 4) {
      setRelationCriteriaObj(data);
    } else if (categoryId === 5) {
      setValueCriteriaObj(data);
    }
  };

  const handleStakeholdersSelect = (data) => {
    setValue("selectedStakeholders", data);
  };

  const handleStepId = (id) => {
    setStepId(id);
    if (id === 3) setNextButtonName("Publish");
    else if (id === 4) setNextButtonName("Published");
    else setNextButtonName("Save & Next");
  };

  const handlePeriodChange = (value) => {
    setState({
      ...state,
      period: value,
    });
  };

  const handleFiscalYearChange = (value) => {
    setState({
      ...state,
      fiscalYear: value,
    });
  };

  const handleSupplierChange = (supplierName, supplierNumber) => {
    setState({
      ...state,
      supplierNumber: supplierNumber,
      supplierName: supplierName,
    });
  };

  const getSteps = () => {
    return CAMPAIGN_STEPS;
  };

  const steps = getSteps();

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <CampaignSuppliers
            control={control}
            errors={errors}
            setValue={setValue}
            currentData={state}
            onPeriodChange={handlePeriodChange}
            onFiscalYearChange={handleFiscalYearChange}
            onSupplierChange={handleSupplierChange}
            onSelectStep={handleStepId}
          />
        );
      case 1:
        return (
          supplierSubFunctionalArea &&
          supplierFunctionalArea && (
            <CampaignCriteria
              control={control}
              errors={errors}
              onSelect={handleCriteriaSelect}
              currentData={state}
              onSelectStep={handleStepId}
              supplierFunctionalArea={supplierFunctionalArea}
              supplierSubFunctionalArea={supplierSubFunctionalArea}
              isCriteriaError={isCriteriaError}
              ratingTeam={ratingTeam}
            />
          )
        );
      case 2:
        return (
          <CampaignStakeHolders
            control={control}
            errors={errors}
            onSelect={handleStakeholdersSelect}
            campaignId={campaignId}
            currentData={state}
            onSelectStep={handleStepId}
            isError={isError}
            errorMsg={stakeholderErrorMsg}
            selectedStakeholders={selectedStakeholders}
            setSelectedStakeholders={setSelectedStakeholders}
            stakeholderList={stakeholderList}
            setStakeholderList={setStakeholderList}
          />
        );
      case 3:
        return (
          <CampaignReview
            currentData={state}
            control={control}
            onEdit={handleEditStep}
            onSelectStep={handleStepId}
          />
        );
      case 4:
        return (
          <CampaignPublish
            currentData={state}
            control={control}
            onSelectStep={handleStepId}
            loading={isLoading}
          />
        );
      default:
        return "Unknown step";
    }
  };

  const handleNext = () => {
    setIsNext(true);
  };

  const handleSelectedStep = (step) => {
    setActiveStep(step);
    if (step === 3) setNextButtonName("Publish");
    else if (step === 4) setNextButtonName("Published");
    else setNextButtonName("Save & Next");
  };

  const handleEditStep = (step) => {
    setActiveStep(step);
  };

  const updateState = () => {
    if (isNext) {
      setIsSavingNext(false);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      setIsSaving(false);
    }
  };

  const getSupplierDetails = async () => {
    try {
      return await authAxios.get(`/supplierMnt`).then((res) => {
        res.data.forEach((supplier) => {
          if (supplier.supplierNumber === state.supplierNumber) {
            setSupplierFunctionalArea(supplier.functionalArea);
            setSupplierSubFunctionalArea(supplier.subFunctionalArea);
          }
        });
      });
    } catch (e) {
      console.error(e);
    }
  };
  const processData = (data) => {
    const tempSupplierName2 = data.supplierNameLine2
      ? data.supplierNameLine2
      : "";
    const tempTitle = data.title
      ? data.title
      : data.supplierNumber
      ? `FY${data.fiscalYear} ${
          data.period.description
        }, ${toCapitalizeSplitStr(
          data.supplierName
        )}${tempSupplierName2} - ${parseInt(data.supplierNumber)}`
      : "";

    function getCategoryCriteriaId(categoryID) {
      return data.criteria
        .filter((item) => item.category.id === categoryID)
        .map((item) => {
          return item.id;
        });
    }

    function getCategoryCriteriaData(categoryID) {
      return data.criteria
        .filter((item) => item.category.id === categoryID)
        .map((item) => {
          return item;
        });
    }

    getCriteriaCatalog(data.functionalArea?.id).then((res) => {
      if (data.criteria.length === 0 && data.functionalArea !== null) {
        let selectedFunctionalAreas = [data.functionalArea].map(
          (item) => item.id
        );
        const filterFunctionalArea =
          res.data &&
          res.data.filter((item) => {
            const currentFunctionalAreas = item.functionalAreas.map(
              (functionalArea) => functionalArea.id
            );
            return (
              selectedFunctionalAreas &&
              selectedFunctionalAreas.some((ele) =>
                currentFunctionalAreas.includes(ele)
              )
            );
          });

        const qualityFilterData = filterFunctionalArea
          .filter((item) => item.category.id === 1)
          .map((item) => {
            return item;
          });
        setQualityCriteriaObj(qualityFilterData);

        const deliveryFilterData = filterFunctionalArea
          .filter((item) => item.category.id === 2)
          .map((item) => {
            return item;
          });
        setDeliveryCriteriaObj(deliveryFilterData);

        const processAllignmentFilterData = filterFunctionalArea
          .filter((item) => item.category.id === 3)
          .map((item) => {
            return item;
          });
        setProcessCriteriaObj(processAllignmentFilterData);

        const relationshipFilterData = filterFunctionalArea
          .filter((item) => item.category.id === 4)
          .map((item) => {
            return item;
          });
        setRelationCriteriaObj(relationshipFilterData);

        const valueCreationFilterData = filterFunctionalArea
          .filter((item) => item.category.id === 5)
          .map((item) => {
            return item;
          });
        setValueCriteriaObj(valueCreationFilterData);
      }
    });

    setQualityCriteriaObj(
      getCategoryCriteriaData(1).length > 0
        ? getCategoryCriteriaData(1)
        : getCriteriaCatalog(data.functionalArea?.id)
    );
    setDeliveryCriteriaObj(
      getCategoryCriteriaData(2).length > 0
        ? getCategoryCriteriaData(2)
        : getCriteriaCatalog(data.functionalArea?.id)
    );
    setProcessCriteriaObj(
      getCategoryCriteriaData(3).length > 0
        ? getCategoryCriteriaData(3)
        : getCriteriaCatalog(data.functionalArea?.id)
    );
    setRelationCriteriaObj(
      getCategoryCriteriaData(4).length > 0
        ? getCategoryCriteriaData(4)
        : getCriteriaCatalog(data.functionalArea?.id)
    );
    setValueCriteriaObj(
      getCategoryCriteriaData(5).length > 0
        ? getCategoryCriteriaData(5)
        : getCriteriaCatalog(data.functionalArea?.id)
    );
    setState({
      ...state,
      createdBy: data.createdBy,
      supplierName: data.supplierName,
      supplierNumber: data.supplierNumber,
      period: data.period,
      fiscalYear: data.fiscalYear,
      dueDate: data.dueDate,
      qualityCriteriaData: getCategoryCriteriaId(1),
      deliverCriteriaData: getCategoryCriteriaId(2),
      processAlignmentCriteriaData: getCategoryCriteriaId(3),
      relationshipCriteriaData: getCategoryCriteriaId(4),
      valueCreationCriteriaData: getCategoryCriteriaId(5),
      criteriaSelectedData: data.criteria,
      stakeholders: data.stakeholders,
      description: data.description,
      title: tempTitle,
      status: data.status,
      functionalArea: data.functionalArea,
    });
    setActiveStep(data.currentStep);
    setIsLoading(false);
  };

  const getCampaignData = async (id) => {
    await authAxios
      .get(`campaign/${id}`)
      .then((res) => {
        const data = res.data;
        setCampaignId(data.id);
        processData(data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getCriteriaCatalog = async (id) => {
    try {
      const data = {
        categories: [1, 2, 3, 4, 5],
        status: "active",
        functionalGroupId: id
      };
      return await authAxios.post(`/criteria/search`, data);
    } catch (e) {
      console.error(e);
    }
  };

  const onCancel = () => {
    history.push("/campaigns");
  };

  const validateUserName = (arr) => {
    return arr.some((item) => {
      return item.user && Object.keys(item.user).length === 0;
    });
  };

  const postCampaignData = (postData) => {
    authAxios
      .post(`/campaign`, postData)
      .then((res) => {
        getCampaignData(res.data.id);
        enqueueToast({
          variant: "success",
          title: "Saved Successfully",
          isCloseable: true,
        });
        updateState();
        reset();
      })
      .catch((error) => {
        enqueueToast({
          variant: "error",
          title: "System Error",
          message: error.response.data.message ?? "",
          isPersistent: true,
        });
        console.log(error);
      });
  };

  const updateCampaignData = (putData) => {
    setIsLoading(true);
    let successMsg = "Saved Successfully";
    if (putData.currentStep === 4) successMsg = "Campaign Published";
    authAxios
      .put(`/campaign/${campaignId}`, putData)
      .then((res) => {
        getCampaignData(campaignId);
        updateState();
        enqueueToast({
          variant: "success",
          title: successMsg,
          isCloseable: true,
        });
        reset();
      })
      .catch((error) => {
        console.log(error.response);
        enqueueToast({
          variant: "error",
          title: error.response?.data?.error ?? "System Error",
          message: error.response?.data?.[error.response.status]
            ?? error.response.data.message ?? "",
          isPersistent: true,
        });
        setActiveStep(putData.currentPage);
        setIsLoading(false);
      });
  };

  const onSubmit = async (data) => {
    if (isDatePast(data.responseByDate)) {
      setError("responseByDate", {
        type: "manual",
        message: "Select a date in future",
      });
      return false;
    }
    if (isNext) setIsSavingNext(true);
    else setIsSaving(true);
    if (stepId === 0) {
      if (!campaignId) {
        const postData = {
          ...data,
          currentStep: isNext ? stepId + 1 : stepId,
          currentPage: stepId,
        };
        postCampaignData(postData);
      } else {
        const putData = {
          dueDate: data.responseByDate,
          status: "Not yet published",
          fiscalYear: data.fiscalYear,
          period: {
            id: data.period,
            description: "",
          },
          supplierName: "",
          supplierNumber: data.selectedSupplier,
          currentStep: isNext ? stepId + 1 : stepId,
          currentPage: stepId,
        };
        updateCampaignData(putData);
      }
    } else if (stepId === 1) {
      const isQualityCriteriaObj =
        qualityCriteriaObj.length > 0 ? qualityCriteriaObj : "";
      const isDeliveryCriteriaObj =
        deliveryCriteriaObj.length > 0 ? deliveryCriteriaObj : "";
      const isProcessCriteriaObj =
        processCriteriaObj.length > 0 ? processCriteriaObj : "";
      const isRelationCriteriaObj =
        relationCriteriaObj.length > 0 ? relationCriteriaObj : "";
      const isValueCriteriaObj =
        valueCriteriaObj.length > 0 ? valueCriteriaObj : "";
      const selectedCriteria = [
        ...isQualityCriteriaObj,
        ...isDeliveryCriteriaObj,
        ...isProcessCriteriaObj,
        ...isRelationCriteriaObj,
        ...isValueCriteriaObj,
      ];
      setValue("selectedCriteriaValue", selectedCriteria);
      const selectedRowsData = getValues("selectedCriteriaValue");
      // const criteriaObj = selectedCriteria.length > 0 && selectedCriteria;
      const criteriaObj =
        selectedCriteria &&
        selectedCriteria.map((item) => {
          return {
            id: item.id,
            criteria: item.criteria,
            category: {
              id: "",
              description: "",
            },
            subCategory: {
              id: item.subCategory && item.subCategory.id,
              description: item.subCategory && item.subCategory.description,
            },
            stakeholders: item.stakeholders,
          };
        });
      if (criteriaObj.length === 0 && selectedRowsData.length === 0) {
        setIsCriteriaError(true);
      } else {
        const putData = {
          criteria: [...criteriaObj],
          currentStep: isNext ? stepId + 1 : stepId,
          currentPage: stepId,
        };
        updateCampaignData(putData);
        setIsCriteriaError(false);
      }
    } else if (stepId === 2) {
      const selectedData = getValues("selectedStakeholders");
      if (selectedData.length === 0) {
        setIsError(true);
        setStakeholderErrorMsg("Please select a Stakeholder");
      } else if (validateUserName(selectedData)) {
        setIsError(true);
        setStakeholderErrorMsg("User Name is mandatory for selected rows");
      } else {
        const putData = {
          stakeholders: getValues("selectedStakeholders"),
          currentStep: isNext ? stepId + 1 : stepId,
          currentPage: stepId,
        };
        updateCampaignData(putData);
        setIsError(false);
      }
    } else if (stepId === 3) {
      const campaignDesc = getValues("campaignDesc");
      const campaignTitle = getValues("campaignTitle");
      const putData = {
        title: campaignTitle,
        description: campaignDesc,
        currentStep: isNext ? stepId + 1 : stepId,
        currentPage: stepId,
      };
      updateCampaignData(putData);
    }
    setIsSavingNext(false);
    setIsSaving(false);
    setIsNext(false);
  };

  const onDelete = async () => {
    await authAxios
      .delete(`/campaign/${campaignId}`)
      .then((res) => {
        enqueueToast({
          variant: "success",
          title: "Campaign Deleted Successfully",
          isCloseable: true,
        });
        setTimeout(() => {
          history.replace(`/campaigns`);
        }, 2000);
      })
      .catch((error) => {
        enqueueToast({
          variant: "error",
          title: "System Error",
          isPersistent: true,
        });
      });
  };

  useEffect(() => {
    if (campaignId) {
      getCampaignData(campaignId);
    }
    if (editCampaignId) {
      setIsLoading(true);
      getCampaignData(editCampaignId);
    }
    return () => {
      setIsError(false);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    getSupplierDetails();

    if (state.status === "Published") {
      setDisable(true);
    }
    return () => {
      setIsError(false);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const rolesNotIncluded = stakeholderList.filter(
    (role) => !selectedStakeholders.includes(role?.id) && (!role.user?.racfID || !role.unit?.description)
  );

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data))}>
      <Grid container alignItems="center">
        <Grid item xs={12} md={7} className="sticky-header">
          <Typography variant="h1" component="h1">
            {state.title ? state.title : name}
          </Typography>
          <Typography variant="h4">
            {state.fiscalYear &&
            supplierFunctionalArea &&
            supplierSubFunctionalArea
              ? `Rated as ${supplierFunctionalArea.description} - ${supplierSubFunctionalArea.description}, FY${state.fiscalYear}, ${state.period.description}`
              : ""}
          </Typography>
        </Grid>
        <Grid item xs={12} md={5} className="sticky-header">
          <Box display="flex" justifyContent="flex-end" mt={4}>
            {state.status === "Published" ? (
              <Button
                variant="secondary"
                style={{ marginRight: "1rem" }}
                size="large"
                onClick={() => {
                  onCancel();
                }}
              >
                Back to Campaigns
              </Button>
            ) : (
              <>
                {editCampaignId &&
                  (isCriteriaAdmin ||
                    (loggedInUserId &&
                      loggedInUserId.toUpperCase() === state.createdBy)) && (
                    <ModalDialog
                      titleText={"Delete Campaign"}
                      content={
                        <React.Fragment>
                          Are you sure you want to delete this Campaign?
                        </React.Fragment>
                      }
                      saveButtonText={"Delete"}
                      onSave={onDelete}
                      page={"Campaign"}
                    />
                  )}
                <Button
                  variant="secondary"
                  style={{ marginRight: "1rem" }}
                  size="large"
                  onClick={() => {
                    onCancel();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  style={{ marginRight: "1rem" }}
                  size="large"
                  type="submit"
                  startIcon={
                    isSaving ? (
                      <CircularProgress color="inherit" size="sm" />
                    ) : (
                      <></>
                    )
                  }
                >
                  Save
                </Button>
                <Button
                  variant="primary"
                  size="large"
                  type="submit"
                  startIcon={
                    isSavingNext ? (
                      <CircularProgress color="inherit" size="sm" />
                    ) : (
                      <></>
                    )
                  }
                  onClick={() => {
                    handleNext();
                  }}
                >
                  {nextButtonName}
                </Button>
              </>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <StepperComponent
            steps={steps}
            disable={disable}
            activeStep={activeStep}
            onClick={handleSelectedStep}
            disableStepNum={4}
          />
        </Grid>
        {!!rolesNotIncluded?.length && activeStep !== 0 && (
          <Grid item xs={12}>
            <Box sx={{ mr: 6, mt: 6 }}>
              <Alert
                title="The following roles are not included in this campaign"
                variant="warning"
                component={"div"}
              >
                <ul>
                  {rolesNotIncluded.map((role, index) => (
                    <li key={`notIncludedRole-${index}`}>
                      <b>{role?.role?.description}</b> is assigned to{" "}
                      <b>{role?.user?.racfID ?? "[NO USER]"}</b> for unit{" "}
                      <b>{role?.unit?.description ?? "[NO UNIT]"}</b>
                    </li>
                  ))}
                </ul>
              </Alert>
            </Box>
          </Grid>
        )}
        {isLoading && (
          <>
            <Loading />
          </>
        )}
        {!isLoading && getStepContent(activeStep)}
      </Grid>
    </form>
  );
};

CampaignCreate.propTypes = {
  name: PropTypes.string,
};
